import { useState, useEffect, useCallback } from 'react';
import type { UserMonitorConfig } from '../types/UserMonitorConfig';
import { LOCAL_STORAGE_KEY_USER_MONITOR, SCALING_FACTORS } from '../constants';

const DEFAULT_CONFIG: UserMonitorConfig = {
  diagonalInches: 27,
  nativeResolutionW: 2560,
  nativeResolutionH: 1440,
  // 2× default scaling → 1280×720
  scalingLogicalW: 1280,
  scalingLogicalH: 720,
};

/**
 * Migrate a stored config that may still use the old `scalingMode` field
 * (stored before the ScalingPreset refactor) to the new `scalingLogicalW/H` shape.
 */
function migrateConfig(raw: Record<string, unknown>): UserMonitorConfig {
  if (typeof raw.scalingLogicalW === 'number' && typeof raw.scalingLogicalH === 'number') {
    // Already new format
    return raw as unknown as UserMonitorConfig;
  }
  // Old format: derive logical resolution from scalingMode
  const nativeW = typeof raw.nativeResolutionW === 'number' ? raw.nativeResolutionW : DEFAULT_CONFIG.nativeResolutionW;
  const nativeH = typeof raw.nativeResolutionH === 'number' ? raw.nativeResolutionH : DEFAULT_CONFIG.nativeResolutionH;
  const mode = typeof raw.scalingMode === 'string' ? raw.scalingMode : 'default';
  const factor = (SCALING_FACTORS as Record<string, number>)[mode] ?? SCALING_FACTORS.default;
  return {
    diagonalInches: typeof raw.diagonalInches === 'number' ? raw.diagonalInches : DEFAULT_CONFIG.diagonalInches,
    nativeResolutionW: nativeW,
    nativeResolutionH: nativeH,
    scalingLogicalW: Math.round(nativeW / factor),
    scalingLogicalH: Math.round(nativeH / factor),
  };
}

function readFromStorage(): UserMonitorConfig | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY_USER_MONITOR);
    if (!raw) return null;
    return migrateConfig(JSON.parse(raw) as Record<string, unknown>);
  } catch {
    return null;
  }
}

function writeToStorage(config: UserMonitorConfig): void {
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_MONITOR, JSON.stringify(config));
}

export interface UseUserMonitorReturn {
  /** The user's current monitor configuration. Never null after initialisation. */
  config: UserMonitorConfig;
  /** Whether the user has explicitly saved their config (vs using the default). */
  isConfigured: boolean;
  /** Persist an updated config. */
  setConfig: (config: UserMonitorConfig) => void;
  /** Reset to the default placeholder values and clear localStorage. */
  resetConfig: () => void;
}

/**
 * Reads, writes, and persists the user's own monitor spec in localStorage.
 * Components should consume this hook rather than reading localStorage directly.
 */
export function useUserMonitor(): UseUserMonitorReturn {
  const [config, setConfigState] = useState<UserMonitorConfig>(() => readFromStorage() ?? DEFAULT_CONFIG);
  const [isConfigured, setIsConfigured] = useState<boolean>(() => readFromStorage() !== null);

  useEffect(() => {
    const stored = readFromStorage();
    if (stored) {
      setConfigState(stored);
      setIsConfigured(true);
    }
  }, []);

  const setConfig = useCallback((next: UserMonitorConfig) => {
    writeToStorage(next);
    setConfigState(next);
    setIsConfigured(true);
  }, []);

  const resetConfig = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY_USER_MONITOR);
    setConfigState(DEFAULT_CONFIG);
    setIsConfigured(false);
  }, []);

  return { config, isConfigured, setConfig, resetConfig };
}

